import Decimal from "decimal.js";

// 金額一律用 decimal.js 運算,絕不使用 JS number,避免浮點誤差。
// 進位一律 ROUND_HALF_UP,與後端 Python 的 Decimal 行為對齊。
Decimal.set({ rounding: Decimal.ROUND_HALF_UP, precision: 30, toExpNeg: -30, toExpPos: 30 });

/** 單行金額的小數位數(分)。 */
export const AMOUNT_DP = 2;

/** 會被視為千分位或裝飾用、可安全去除的字元:半形/全形逗號、空白、不斷行空白、貨幣符號。 */
const DECORATION = /[,，\s $＄]/g;

/**
 * 可接受的數字形式:可選正負號 + 數字 + 可選一個小數點 + 可選科學記號。
 * 正號與科學記號是刻意收下的 —— 後端的 `Decimal()` 讀得懂 "+500" 與 "1e3",
 * 前端若把它們當成 0,畫面就會少算而存檔照存,這種靜默差異最難發現。
 */
const NUMERIC = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/**
 * 把使用者輸入或 API 字串轉成 Decimal。無法解析時回 0,
 * 讓畫面上的即時總計不會因為一格打錯字就整個炸掉。
 */
export function toDecimal(value: string | number | null | undefined): Decimal {
  const cleaned = parseMoneyInput(value == null ? "" : String(value));
  try {
    return new Decimal(cleaned);
  } catch {
    return new Decimal(0);
  }
}

/**
 * 去掉千分位逗號與空白後回傳純數字字串;無法解析時回 "0"。
 * 回傳字串而非 number,因為金額全程以字串在前後端之間傳遞。
 */
export function parseMoneyInput(raw: string): string {
  if (raw == null) return "0";
  const cleaned = String(raw).replace(DECORATION, "");
  if (cleaned === "" || !NUMERIC.test(cleaned)) return "0";
  return cleaned;
}

/**
 * 顯示用格式化:**全系統金額一律 0,000,000,有小數就四捨五入成整數**(業主 2026-10-15)。
 * 例:"123456.00" → "123,456";"1234.50" → "1,235";"-2000.00" → "-2,000"。
 * 只管顯示:計算與存檔照舊保留兩位小數,合計算完才在這裡進位。
 */
export function formatMoney(value: string | number | null | undefined): string {
  return formatWholeMoney(value);
}

/**
 * 單價的顯示格式。業主原話:「報價單內的單價 不用小數點 但要規範數字規格 0,000,000這樣」。
 * "38000" → "38,000";"38000.00" → "38,000";"1234.5" → "1,235";"0" → "0";"-2000" → "-2,000"。
 *
 * 與 formatMoney 的差別是這裡**一律不留小數**:單價連輸入都只能是整數,
 * 畫面上看到的數字就是真正拿去算的數字。
 */
export function formatWholeMoney(value: string | number | null | undefined): string {
  const whole = toDecimal(value).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  const digits = whole.abs().toFixed(0);
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return whole.isNegative() && !whole.isZero() ? `-${grouped}` : grouped;
}

/**
 * 單價的輸入正規化:去掉千分位與空白,四捨五入(ROUND_HALF_UP)成整數字串;
 * 非數字回 "0"。進位規則與後端 `pricing.to_whole_dollars` 是同一條。
 *
 * 全程走 decimal.js,**不經過 parseFloat / Number** —— 9007199254740993 這種
 * 超過 JS number 精度的數字一旦被轉過一次就再也回不來,而且錯得無聲無息。
 */
export function parseWholeMoney(raw: string): string {
  return toDecimal(raw).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toFixed(0);
}

/**
 * 送回後端用的正規化字串,固定兩位小數(如 "1234.00")。
 * 後端仍會重算,這裡只保證格式一致。
 */
export function toAmountString(value: string | number | null | undefined): string {
  return toDecimal(value).toDecimalPlaces(AMOUNT_DP, Decimal.ROUND_HALF_UP).toFixed(AMOUNT_DP);
}

/** 依序相加,供前端即時預覽小計使用(存檔時仍以後端重算為準)。 */
export function sumMoney(values: Array<string | number | null | undefined>): string {
  return values
    .reduce<Decimal>((acc, v) => acc.plus(toDecimal(v)), new Decimal(0))
    .toDecimalPlaces(AMOUNT_DP, Decimal.ROUND_HALF_UP)
    .toFixed(AMOUNT_DP);
}

/** 相乘後四捨五入到分,對應計價引擎的單行金額規則。 */
export function multiplyMoney(...values: Array<string | number | null | undefined>): string {
  return values
    .reduce<Decimal>((acc, v) => acc.times(toDecimal(v)), new Decimal(1))
    .toDecimalPlaces(AMOUNT_DP, Decimal.ROUND_HALF_UP)
    .toFixed(AMOUNT_DP);
}

/* ---------- 稅率:資料庫存小數,畫面用百分比 ---------- */

/** 稅率的小數位數。資料表是 NUMERIC(5,4),與後端 `pricing.RATE_PLACES` 同一個數字。 */
export const RATE_DP = 4;

/** 百分比與小數之間的倍率。寫成 Decimal 是為了讓除法留在十進位裡。 */
const PERCENT = new Decimal(100);

/**
 * 把後端給的小數稅率換成畫面上的百分比字串。
 * 業主 2026-09-11:「稅率顯示不要 0.0500 直接單位是 %數」。
 *
 * "0.0500" → "5";"0.0000" → "0";"0.0525" → "5.25";"1.0000" → "100"。
 * 尾隨的零一律抹掉 —— 欄位裡出現「5.0000」跟 0.0500 一樣難讀。
 * 這是後端 `pricing.rate_to_percent` 的鏡像,兩邊各有測試釘住 5 ↔ 0.0500。
 */
export function rateToPercent(rate: string | number | null | undefined): string {
  // toFixed() 不帶參數:一律一般寫法(decimal.js 不會吐 1e+2),也不補尾零。
  return toDecimal(rate).times(PERCENT).toFixed();
}

/**
 * 把畫面上打進來的百分比換回資料庫存的小數稅率。
 * "5" → "0.0500";"5.25" → "0.0525";空字串或看不懂的字 → "0.0000"。
 *
 * **一律走 decimal.js 除法,不經過 parseFloat / Number。**
 * 7 / 100 在 float 下是 0.07000000000000001,存成稅率、乘上六位數的小計,
 * 算出來的稅就會差一分錢 —— 而報價單上的每一塊錢都要對得起來。
 */
export function percentToRate(percent: string | number | null | undefined): string {
  return toDecimal(percent).div(PERCENT).toDecimalPlaces(RATE_DP, Decimal.ROUND_HALF_UP).toFixed(RATE_DP);
}

/**
 * 送出前的稅率正規化:固定四位小數,與資料表的 NUMERIC(5,4) 同一個格式。
 *
 * 用固定格式而不是「有幾位寫幾位」,存檔前後拿到的字串才會是同一個 ——
 * 送 "0.05" 回來 "0.0500",草稿與後端就永遠對不起來,存完檔還顯示未存檔。
 */
export function toRatePayload(value: string | number | null | undefined): string {
  return toDecimal(value).toDecimalPlaces(RATE_DP, Decimal.ROUND_HALF_UP).toFixed(RATE_DP);
}

/**
 * 人數欄的輸入清理:只留可選負號與其後的連續數字,在第一個非數字處截斷。
 * 「3.7」→「3」而不是「37」——後端的人數是整數欄,把小數點濾掉再讓後面的
 * 數字接上去會把 3.7 變成 37,那比截斷更危險。
 */
export function sanitizeCountInput(raw: string): string {
  const match = /^-?\d*/.exec(String(raw ?? "").trim());
  return match ? match[0] : "";
}

/**
 * 人數的計算值:往零截斷成整數。與 sanitizeCountInput 同一套規則,
 * 前端預覽的數字才會與後端存下的數字一致。
 */
export function toCountString(value: string | number | null | undefined): string {
  return toDecimal(value).toDecimalPlaces(0, Decimal.ROUND_DOWN).toFixed(0);
}

/**
 * 送出前的數量/天數正規化:未填維持未填(null),其餘化成後端讀得懂的十進位字串。
 * 從 Excel 貼進來的 "1,200" 在這裡變成 "1200",不會讓整份存檔換來一個 422。
 */
export function toQtyPayload(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  return toDecimal(value).toFixed();
}

/** 送出前的金額正規化:未填維持未填,其餘固定兩位小數。 */
export function toAmountPayload(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  return toAmountString(value);
}

/** 送出前的人數正規化:未填維持未填,其餘取整數。 */
export function toCountPayload(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const cleaned = sanitizeCountInput(String(value));
  return cleaned === "" || cleaned === "-" ? null : cleaned;
}
